import ChevronDownIcon from '@heroicons/react/20/solid/ChevronDownIcon.js';
import React, { useContext } from 'react';
import {
  Button,
  Disclosure as DisclosureRoot,
  DisclosurePanel as DisclosurePanelRoot,
  DisclosureStateContext,
} from 'react-aria-components';

import { tv } from '../../tv';
import { Icon } from '../icon';
import { SlotPadding } from '../slots/slot-context';

/**
 * A section that opens and closes: a trigger row and the panel it reveals.
 *
 * The behaviour is React Aria's: the trigger is a real button carrying
 * aria-expanded and aria-controls, the panel is hidden from assistive
 * technology while closed, and `hidden="until-found"` lets the browser's
 * find-in-page open a section that contains the match.
 *
 * The panel animates without measuring anything. Its grid row goes from 0fr
 * to 1fr and the browser does the arithmetic, which is what lets a height
 * reach `auto` in CSS alone; React Aria waits for the transition to finish
 * before hiding the content, so the close animates as well as the open. The
 * chevron turns over the same duration, so the two read as one movement.
 *
 * The trigger sets no type size or weight. A disclosure sits at every
 * altitude, a settings section and an agent's tool call among them, and the
 * label inside it is a Text or Heading that already carries its own step.
 */

const disclosureStyles = tv({
  slots: {
    root: '',
    /* A ghost row: bare at rest, an alpha-3 surface when pointed at, one
     * step deeper while pressed (design-system.md, variant vocabulary). The
     * states are React Aria's data attributes through the plugin variants, so
     * a tap never leaves a hover surface stuck. The surface bleeds one step
     * past the row on both sides (negative margin, matching padding), so the
     * row's text keeps the column's edge while the highlight has room. */
    trigger: [
      'ck:flex ck:w-full ck:items-center ck:gap-2 ck:rounded-2',
      'ck:cursor-pointer ck:bg-transparent ck:text-left ck:text-inherit',
      'ck:transition-chrome',
      'ck:hover:bg-neutral-a-3 ck:pressed:bg-neutral-a-4',
      'ck:focus-visible:focus-ring',
      'ck:disabled:cursor-not-allowed ck:disabled:opacity-50',
    ],
    /* The wrapper turns rather than the glyph, because Icon owns the glyph's
     * own classes; it is a flex box so the rotation happens about the icon's
     * centre and not a text baseline. The 20-grid solid chevron reads lighter
     * at 12px than the 24-grid outline one, which is what a dense row wants. */
    chevron: [
      'ck:inline-flex ck:shrink-0',
      'ck:transition-transform ck:duration-base ck:ease-standard ck:motion-reduce:transition-none',
    ],
    /* min-w-0 on both: a grid item's minimum width defaults to its content,
     * so a long unbroken value inside would widen the panel past its column. */
    panel: 'ck:grid ck:min-w-0 ck:transition-rows ck:motion-reduce:transition-none',
    /* min-h-0 is what lets the row reach 0fr: a grid item's minimum height
     * defaults to its content, and the clip hides what the row cannot show. */
    clip: 'ck:min-h-0 ck:min-w-0 ck:overflow-hidden',
    content: '',
  },
  variants: {
    expanded: {
      true: { chevron: 'ck:rotate-0', panel: 'ck:grid-rows-expanded' },
      false: { chevron: 'ck:-rotate-90', panel: 'ck:grid-rows-collapsed' },
    },
    /* A row that already carries its own rhythm (a list row, a table row)
     * asks for none: the hover surface then bleeds one step past the text on
     * each side and the text stays on the column's edge. The default is the
     * inset a bare label needs. */
    triggerPadding: {
      default: { trigger: 'ck:px-2 ck:py-1' },
      none: { trigger: 'ck:-mx-1 ck:w-bleed ck:px-1' },
    },
    /* The same steps the shared slots read, so a panel inside a card and the
     * card's own content agree on inset. On the content element rather than
     * the panel: padding on the grid or the clip would still stand at 0fr,
     * and the row would never close fully. */
    padding: {
      none: { content: '' },
      compact: { content: 'ck:p-2' },
      default: { content: 'ck:p-4' },
      spacious: { content: 'ck:p-6' },
    },
  },
  defaultVariants: { expanded: false, padding: 'none', triggerPadding: 'default' },
});

export interface DisclosureProps {
  /** A Disclosure.Trigger followed by a Disclosure.Panel. */
  children?: React.ReactNode;
  /** Whether the panel is open (controlled). */
  isExpanded?: boolean;
  /** Whether the panel starts open (uncontrolled). */
  defaultExpanded?: boolean;
  /** Fires with the new state when the trigger is pressed. */
  onExpandedChange?: (isExpanded: boolean) => void;
  /** Keeps the section in its current state and greys the trigger. */
  isDisabled?: boolean;
}

export interface DisclosureTriggerProps {
  /**
   * The row's label. Typography is inherited, not imposed: pass a Text or a
   * Heading at the step the surrounding composition reads at.
   */
  children: React.ReactNode;
  /**
   * Where the turning chevron sits. Leading is the reading order of a section
   * heading; trailing keeps a dense row's glyph column for the row's own
   * glyph; none is for a row whose openness the content already shows.
   */
  chevron?: 'leading' | 'trailing' | 'none';
  /** Inset around the trigger; none when the children carry their own rhythm. */
  padding?: 'default' | 'none';
  id?: string;
  /** Names the trigger when its children do not read as a name on their own. */
  'aria-label'?: string;
}

export interface DisclosurePanelProps {
  children: React.ReactNode;
  /**
   * Inset around the revealed content, on the shared slot scale. None by
   * default: the content composes its own rhythm, and a panel that always
   * padded would push a nested disclosure off the trigger's own column.
   */
  padding?: SlotPadding;
  id?: string;
}

const Root = React.forwardRef<HTMLDivElement, DisclosureProps>(function Disclosure(
  { children, ...props },
  ref,
) {
  const { root } = disclosureStyles();
  return (
    <DisclosureRoot {...props} ref={ref} className={root()} data-component="disclosure">
      {children}
    </DisclosureRoot>
  );
});

const Trigger = React.forwardRef<HTMLButtonElement, DisclosureTriggerProps>(
  function DisclosureTrigger(
    { children, chevron: placement = 'leading', padding = 'default', ...props },
    ref,
  ) {
    const expanded = useContext(DisclosureStateContext)?.isExpanded ?? false;
    const { trigger, chevron } = disclosureStyles({ expanded, triggerPadding: padding });
    const glyph =
      placement === 'none' ? null : (
        <span className={chevron()} aria-hidden="true">
          <Icon as={ChevronDownIcon} size="sm" />
        </span>
      );
    return (
      /* slot="trigger" is how React Aria hands this button its aria wiring
       * and its press handler; a plain Button here would be inert. */
      <Button {...props} ref={ref} slot="trigger" className={trigger()}>
        {placement === 'leading' ? glyph : null}
        {children}
        {placement === 'trailing' ? glyph : null}
      </Button>
    );
  },
);

const Panel = React.forwardRef<HTMLDivElement, DisclosurePanelProps>(function DisclosurePanel(
  { children, padding = 'none', ...props },
  ref,
) {
  const expanded = useContext(DisclosureStateContext)?.isExpanded ?? false;
  const { panel, clip, content } = disclosureStyles({ expanded, padding });
  return (
    <DisclosurePanelRoot {...props} ref={ref} className={panel()}>
      <div className={clip()}>
        <div className={content()}>{children}</div>
      </div>
    </DisclosurePanelRoot>
  );
});

/* The parts hang off the root, as Page's regions do: a trigger and a panel
 * are the disclosure's own anatomy, not shared slots any container may
 * claim, so they take the component's name rather than the barrel's. */
export const Disclosure = Object.assign(Root, {
  Trigger,
  Panel,
});
